/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";

import {
  CREATE_TEMPLATE_COSTUME_VALUE,
  UPDATE_TEMPLATE_COSTUME_VALUE,
  DELETE_TEMPLATE_COSTUME_VALUE,
} from "../graphql";
import { uploadFile } from "./upload.service";

const log: any = LoggerModule?.getInstance();

export interface ValueFormData {
  name?: string;
  desc?: string;
  description?: string;
  deadline?: string;
  topupPrice?: string | number;
  currency?: string;
  inventory?: string | number;
  disclaimer?: string;
  minDownPayment?: string | number;
  position?: string | number;
  isVisible?: boolean;
  isMandatory?: boolean;
  isLabel?: boolean;
  images?: any[];
  costumeAttribute?: string;
  template_costumeAttribute?: string;
  [key: string]: any;
}

export interface CreateValueProps {
  formData?: ValueFormData;
  [key: string]: any;
}

export interface UpdateValueProps {
  id: string;
  formData?: ValueFormData;
  [key: string]: any;
}

export interface DeleteValueProps {
  id: string;
}

export interface IValueMethods extends IPluginMethodMap {
  createValue(args: CreateValueProps): Promise<any>;
  updateValue(args: UpdateValueProps): Promise<any>;
  deleteValue(args: DeleteValueProps): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getDisplayMessage = (err: any) => {
  const displayMessage =
    err?.graphQLErrors &&
    Array.isArray(err?.graphQLErrors) &&
    err?.graphQLErrors.find((item: any) => item.extensions);

  return (
    displayMessage?.extensions?.display_message ||
    err?.message ||
    "Something went wrong"
  );
};

const buildValuePayload = async (args: any, mode: "create" | "update") => {
  const raw = args?.formData ?? args ?? {};
  const name = raw.name ?? "Untitled Value";
  const uploadedImages =
    Array.isArray(raw?.images) && raw.images.length > 0
      ? await uploadFile(raw.images, name, "costumevalues")
      : undefined;

  const payload: any = {
    costumeAttribute: raw.costumeAttribute ?? raw.template_costumeAttribute,

    name,

    topupPrice:
      raw.topupPrice !== undefined &&
      raw.topupPrice !== null &&
      raw.topupPrice !== ""
        ? String(raw.topupPrice)
        : mode === "create"
        ? "0"
        : undefined,

    currency: raw.currency ?? "ttd",

    inventory:
      raw.inventory !== undefined &&
      raw.inventory !== null &&
      raw.inventory !== ""
        ? Number(raw.inventory)
        : undefined,

    desc: raw.desc ?? raw.description,

    isVisible: raw.isVisible === true,
    isMandatory: raw.isMandatory === true,

    disclaimer: raw.disclaimer,

    minDownPayment:
      raw.minDownPayment !== undefined &&
      raw.minDownPayment !== null &&
      raw.minDownPayment !== ""
        ? String(raw.minDownPayment)
        : undefined,

    position:
      raw.position !== undefined && raw.position !== null && raw.position !== ""
        ? String(raw.position)
        : undefined,

    isLabel: raw.isLabel === true,
  };

  if (uploadedImages !== undefined) {
    payload.images = uploadedImages;
  }

  if (raw.deadline !== undefined) {
    payload.deadline =
      raw.deadline !== null && String(raw.deadline).trim() !== ""
        ? raw.deadline
        : null;
  }

  if (mode === "create") {
    payload.presentation = raw.presentation;
  }

  if (mode === "update") {
    delete payload.presentation;
    delete payload.costumeAttribute;
  }

  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined) {
      delete payload[key];
    }
  });

  return payload;
};

export class ValueService implements IAgentPlugin {
  readonly methods: IValueMethods;

  constructor() {
    this.methods = {
      createValue: this.createValue.bind(this),
      updateValue: this.updateValue.bind(this),
      deleteValue: this.deleteValue.bind(this),
    };
  }

  async createValue(args: CreateValueProps) {
    log?.blue?.("ValueService.createValue:", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (!client) {
        throw new Error("GraphQL client not found");
      }

      const data = await buildValuePayload(args, "create");
      const res = await client.mutate({
        mutation: CREATE_TEMPLATE_COSTUME_VALUE,
        variables: {
          data,
        },
        fetchPolicy: "no-cache",
      });

      return res?.data?.createCostumeValue;
    } catch (err: any) {
      throw new Error(getDisplayMessage(err));
    }
  }

  async updateValue(args: UpdateValueProps) {
    log?.blue?.("ValueService.updateValue:", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (!client) {
        throw new Error("GraphQL client not found");
      }

      if (!args?.id) {
        throw new Error("ID not found for update");
      }

      const update = await buildValuePayload(args, "update");
      const res = await client.mutate({
        mutation: UPDATE_TEMPLATE_COSTUME_VALUE,
        variables: {
          filter: {
            _id: args.id,
          },
          update,
        },
        fetchPolicy: "no-cache",
      });

      return res?.data?.updateCostumeValue;
    } catch (err: any) {
      throw new Error(getDisplayMessage(err));
    }
  }

  async deleteValue(args: DeleteValueProps) {
    log?.blue?.("ValueService.deleteValue:", args);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (!client) {
        throw new Error("GraphQL client not found");
      }

      if (!args?.id) {
        throw new Error("Missing ID for delete");
      }

      const res = await client.mutate({
        mutation: DELETE_TEMPLATE_COSTUME_VALUE,
        variables: {
          filter: {
            _id: args.id,
          },
        },
        fetchPolicy: "no-cache",
      });

      return res?.data?.deleteCostumeValue;
    } catch (err: any) {
      throw new Error(getDisplayMessage(err));
    }
  }
}
